import type { VentaProducto } from "../types/caja.types";

/** Subtotal cobrado en una línea del ticket (coincide con lógica de ticket térmico). */
export function subtotalLineaVentaProducto(p: VentaProducto): number {
  const peso = p.peso ?? 0;
  const isPeso = peso > 0;
  if (isPeso && (p.precioPorKilo ?? 0) > 0) {
    return Math.round(peso * (p.precioPorKilo ?? 0));
  }
  /** Algunos tickets guardan el total de línea ya en `precioUnitario` (venta por peso). */
  if (isPeso && (p.precioPorKilo ?? 0) <= 0) {
    return Math.round(p.precioUnitario);
  }
  return Math.round(p.precioUnitario * p.cantidad);
}

/** Clave estable para fusionar cantidades del mismo artículo (misma variante). */
export function ventaProductoLineKey(p: VentaProducto): string {
  if (p.variantKey) return `${p.productoId}|vk:${p.variantKey}`;
  if (p.skuVariante) return `${p.productoId}|sku:${p.skuVariante}`;
  if (p.variantesSeleccionadas?.length) {
    const v = [...p.variantesSeleccionadas]
      .map((x) => `${x.groupId}:${x.optionId}`)
      .sort()
      .join(";");
    return `${p.productoId}|${v}`;
  }
  return p.productoId;
}

export interface LineaProductoVenta {
  nombre: string;
  cantidad: number;
  /** Suma cobrada por esta línea (tras agrupar mismas variantes). */
  subtotal: number;
}

/**
 * Agrupa líneas del ticket: mismo producto + misma variante → una fila con cantidad total (×3).
 * Conserva el orden de aparición en el ticket.
 */
export function aggregateVentaProductosLineas(
  productos: VentaProducto[] | undefined,
): LineaProductoVenta[] {
  if (!productos?.length) return [];
  const map = new Map<string, LineaProductoVenta>();
  for (const p of productos) {
    const key = ventaProductoLineKey(p);
    const cur = map.get(key);
    const add = subtotalLineaVentaProducto(p);
    if (cur) {
      cur.cantidad += p.cantidad;
      cur.subtotal += add;
    } else {
      map.set(key, {
        nombre: p.nombre,
        cantidad: p.cantidad,
        subtotal: add,
      });
    }
  }
  return [...map.values()];
}

/**
 * Una fila por entrada en `venta.productos` (orden del ticket), sin fusionar repeticiones.
 * Usar en historial / PDF del registro para que cada línea cobrada aparezca hacia abajo.
 */
export function lineasVentaProductosEnOrden(
  productos: VentaProducto[] | undefined,
): LineaProductoVenta[] {
  if (!productos?.length) return [];
  return productos.map((p) => ({
    nombre: p.nombre,
    cantidad: p.cantidad,
    subtotal: subtotalLineaVentaProducto(p),
  }));
}

export function formatProductosLineasPlain(
  lines: LineaProductoVenta[],
): string {
  if (!lines.length) return "";
  return lines
    .map((l) => {
      const label = l.cantidad > 1 ? `${l.nombre} ×${l.cantidad}` : l.nombre;
      return `${label} ($${l.subtotal.toLocaleString("es-CO")})`;
    })
    .join(", ");
}
